import React from 'react';
import { ArrowRight } from 'lucide-react';
import { GuestGapDevice } from '../components/Guest/GuestGapDevice';
import { Button } from '../components/UI/primitives/Button';
import styles from './GuestHomePage.module.css';

export interface GuestHomePageProps {
  /** Opens the Prospector discovery conversation (register → prospector). */
  onStartProspector: () => void;
  /** Routes returning users to sign in. */
  onSignIn: () => void;
  /** Opens the public Resources library. */
  onOpenResources?: () => void;
}

export const GuestHomePage: React.FC<GuestHomePageProps> = ({
  onStartProspector,
  onSignIn,
  onOpenResources,
}) => {
  return (
    <main className={styles.landing}>
      <header className={styles.nav}>
        <img src="/krysalis-logo.svg" alt="Krysalis" className={styles.logo} />
        <button type="button" className={styles.signInBtn} data-testid="guest-sign-in-nav" onClick={onSignIn}>
          Already on the bridge? Sign in
        </button>
      </header>

      <section className={styles.hero} aria-labelledby="guest-hero-heading">
        <div className={styles.heroInner}>
          <p className={styles.kicker}>any business · any product or service</p>
          <h1 id="guest-hero-heading" className={styles.h1}>
            Where do you want to take your business?
            <br />
            <span className={styles.h1Secondary}>
              Start there — even if it&apos;s still taking shape.
            </span>
          </h1>
          <p className={styles.heroSub}>
            Impatient to get there? Start a session and let&apos;s get everything rolling — we&apos;ll
            get you to your destination faster.
          </p>
          <GuestGapDevice className={styles.gap} />
          <Button
            className={styles.cta}
            variant="primary"
            size="lg"
            onClick={onStartProspector}
            iconRight={<ArrowRight size={18} aria-hidden />}
          >
            Let&apos;s figure out what you need
          </Button>
        </div>
      </section>

      <section className={styles.band} aria-labelledby="guest-band-heading">
        <div className={styles.bandInner}>
          <h2 id="guest-band-heading" className={styles.h2}>
            Krysalis is the bridge across that gap — the fastest way over.
          </h2>
          <p className={styles.bandBody}>
            You name the destination — Krysalis builds a living model of your business and starts
            moving it there. The work gets coordinated and executed, not just mapped, so you reach
            the other side in a fraction of the time it would take to find your own way across. The
            conversation is the first step onto the bridge.
          </p>
          <p className={styles.finePrint}>
            No demo to sit through. No pricing to decode. Just where you&apos;re headed — and the
            shortest way there.
          </p>
        </div>
      </section>

      <footer className={styles.footer}>
        <p className={styles.footerCopy}>
          © Krysalis — the bridge between intent and execution
        </p>
        {onOpenResources ? (
          <button type="button" className={styles.footerLink} onClick={onOpenResources}>
            Resources
          </button>
        ) : null}
        <button type="button" className={styles.signInBtn} data-testid="guest-sign-in-footer" onClick={onSignIn}>
          Sign in
        </button>
      </footer>
    </main>
  );
};
